import React, { useState, useEffect } from 'react';
import { Mail, Phone, ChevronDown, Globe } from 'lucide-react';
import { topNavLinks as defaultLinks } from '../data/mock';
import { contentApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';

const TopBar = ({ onHeaderLinkClick }) => {
  const [currency, setCurrency] = useState('USD');
  const [headerLinks, setHeaderLinks] = useState(defaultLinks);
  const settings = useSettings();

  // Fetch header links from API
  useEffect(() => {
    const fetchHeaderLinks = async () => {
      try {
        const links = await contentApi.getHeaderLinks();
        if (links && links.length > 0) {
          setHeaderLinks(links.map(link => ({
            label: link.title,
            slug: link.slug,
            href: `#${link.slug}`,
          })));
        }
      } catch (error) {
        console.error('Error fetching header links:', error);
        // Keep using default links
      }
    };
    fetchHeaderLinks();
  }, []);

  const handleLinkClick = (e, link) => {
    e.preventDefault();
    if (onHeaderLinkClick && link.slug) {
      onHeaderLinkClick(link.slug);
    }
  };

  return (
    <div className="bg-gradient-to-r from-teal-700 to-teal-600 dark:from-teal-900 dark:to-teal-800 text-white py-2.5 text-sm" role="banner">
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
        {/* Left side - Email */}
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4" aria-hidden="true" />
          <a 
            href={`mailto:${settings.contact_email}`} 
            className="hover:text-teal-200 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
            aria-label={`Email us at ${settings.contact_email}`}
          >
            {settings.contact_email}
          </a>
        </div>

        {/* Center - Navigation Links */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Quick links">
          {headerLinks.map((link, index) => (
            <button
              key={index}
              onClick={(e) => handleLinkClick(e, link)}
              className="px-3 py-1 rounded-full border border-white/30 hover:bg-white/10 transition-all text-xs font-medium focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Right side - Currency & Phone */}
        <div className="flex items-center gap-4">
          {/* Language/Region Indicator */}
          <div className="hidden lg:flex items-center gap-1 text-white/80">
            <Globe className="w-4 h-4" />
            <span className="text-xs">EN</span>
          </div>

          {/* Currency Selector */}
          <div className="relative group">
            <button 
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-white/50"
              aria-haspopup="listbox"
              aria-label={`Currency: ${currency}`}
            >
              <span className="font-medium">$</span>
              <span>{currency}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            <div 
              className="absolute right-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50"
              role="listbox"
            >
              {['USD', 'EUR', 'GBP', 'CAD'].map((curr) => (
                <button
                  key={curr}
                  onClick={() => setCurrency(curr)}
                  className={`w-full px-3 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/30 first:rounded-t-lg last:rounded-b-lg text-sm transition-colors ${
                    currency === curr ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' : ''
                  }`}
                  role="option"
                  aria-selected={currency === curr}
                >
                  {curr}
                </button>
              ))}
            </div>
          </div>

          {/* Phone */}
          <a
            href={`tel:${settings.contact_phone}`}
            className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-400 rounded-full font-medium hover:bg-teal-50 dark:hover:bg-gray-700 transition-all focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label={`Call us at ${settings.contact_phone}`}
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">{settings.contact_phone}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
